// useHeatmapData.js — fetch + transform pipeline for the heatmap.
// Exposes processed data + derived axis/map state so components stay dumb.
// Cell keys follow `axes` (see utils.js); switching axes re-keys in memory
// without refetching.

import { useEffect, useMemo, useState } from "react";
import {
  ALL_LABEL,
  HOME_AWAY,
  isValidUtc,
  parseScoreString,
  safeNum,
  summaryCells,
  toSeasonId,
} from "./utils";
import { cachedScores, fetchScores } from "../../api";


export default function useHeatmapData(season, axes) {
  const isAllSeasons = season === ALL_LABEL;
  // A pre-rendered home page carries the summary it was built with; start
  // from it so the first render matches the saved HTML (no loading flash).
  const [seeded] = useState(() => (isAllSeasons ? cachedScores("summary=1")?.items : undefined));
  const [scores, setScores] = useState([]);
  const [summary, setSummary] = useState(seeded || []);
  const [loading, setLoading] = useState(!seeded);
  const [error, setError] = useState(false);

  // `background`: keep showing the current data while fresh data loads.
  const fetchData = async ({ background = false } = {}) => {
    if (!background) {
      setLoading(true);
      setError(false);
      setScores([]);
      setSummary([]);
    }

    try {
      // Always fetch summary — it powers the "Latest Scorigami" ribbon and
      // the drawer's all-time count in both single-season and all-seasons
      // views. The per-match fetch is only needed for single-season grids.
      const summaryReq = fetchScores("summary=1");

      if (isAllSeasons) {
        const data = await summaryReq;
        const items = data?.items;
        if (!Array.isArray(items) || items.length === 0) {
          throw new Error("No data returned from API");
        }
        setSummary(items);
      } else {
        const matchesReq = fetchScores(`summary=0&limit=2000&seasonId=${toSeasonId(season)}`);
        const [summaryData, matchesData] = await Promise.all([
          summaryReq,
          matchesReq,
        ]);
        const summaryItems = summaryData?.items || [];
        const matchItems = matchesData?.items;
        if (!Array.isArray(matchItems) || matchItems.length === 0) {
          throw new Error("No data returned from API");
        }
        setSummary(summaryItems);
        setScores(matchItems);
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      // A failed background refresh keeps the data already on screen.
      if (!background) setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData({ background: isAllSeasons && summary.length > 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [season]);

  const processedScores = useMemo(() => {
    if (isAllSeasons) return [];
    const out = [];
    for (const s of scores) {
      let hs = safeNum(s.homeTeamScore);
      let as_ = safeNum(s.awayTeamScore);
      if (hs === null || as_ === null) {
        const parsed = parseScoreString(s.score);
        if (!parsed) continue;
        hs = parsed.a;
        as_ = parsed.b;
      }
      if (!isValidUtc(s.utcDate)) continue;
      const highScore = Math.max(hs, as_);
      const lowScore = Math.min(hs, as_);
      const highIsHome = hs >= as_;
      out.push({
        ...s,
        homeTeamScore: hs,
        awayTeamScore: as_,
        highScore,
        lowScore,
        highIsHome,
        highTeam: highIsHome
          ? { name: s.homeTeam, image: s.homeTeamImage }
          : { name: s.awayTeam, image: s.awayTeamImage },
        lowTeam: highIsHome
          ? { name: s.awayTeam, image: s.awayTeamImage }
          : { name: s.homeTeam, image: s.homeTeamImage },
      });
    }
    return out;
  }, [scores, isAllSeasons]);

  const scoreMap = useMemo(() => {
    if (isAllSeasons) return {};
    const map = {};
    for (const s of processedScores) {
      const key =
        axes === HOME_AWAY
          ? `${s.homeTeamScore}-${s.awayTeamScore}`
          : `${s.highScore}-${s.lowScore}`;
      if (!map[key]) map[key] = [];
      map[key].push(s);
    }
    Object.keys(map).forEach((k) =>
      map[k].sort((a, b) => new Date(b.utcDate) - new Date(a.utcDate))
    );
    return map;
  }, [processedScores, isAllSeasons, axes]);

  const scoreSummaryMap = useMemo(
    () => summaryCells(summary, axes),
    [summary, axes]
  );

  // Every goal count from 0 to the highest seen, so a season with no 7-goal
  // side still gets a 7 row/column (empty cells are the point of the grid).
  const axisValues = useMemo(() => {
    const keys = isAllSeasons ? Object.keys(scoreSummaryMap) : Object.keys(scoreMap);
    let max = -1;
    for (const key of keys) {
      const p = parseScoreString(key);
      if (p) max = Math.max(max, p.a, p.b);
    }
    return Array.from({ length: max + 1 }, (_, i) => i);
  }, [scoreSummaryMap, scoreMap, isAllSeasons]);

  const maxCount = useMemo(() => {
    const counts = isAllSeasons
      ? Object.values(scoreSummaryMap).map((s) => s.count || 0)
      : Object.values(scoreMap).map((arr) => arr.length);
    return counts.length ? Math.max(...counts) || 1 : 1;
  }, [scoreSummaryMap, scoreMap, isAllSeasons]);

  return {
    isAllSeasons,
    loading,
    error,
    scoreMap,
    scoreSummaryMap,
    xAxis: axisValues,
    yAxis: [...axisValues].reverse(),
    maxCount,
    retry: () => fetchData(),
  };
}
